import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, of, take } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import {
  QuestionType,
  QuizQuestion,
} from '../../../models/question.quiz.model';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { ChipColor } from '../../components/inputs/chip/chip.component';
import { DlgEditQuestionComponent } from '../dlg-edit-question/dlg-edit-question.component';
import { QuestionViewComponent } from './question-view/question-view.component';
import { QuestionSearchComponent } from '../question-search/question-search.component';

@Component({
  selector: 'app-question-bank',
  standalone: true,
  imports: [
    AsyncPipe,
    BtnBackComponent,
    QuestionViewComponent,
    QuestionSearchComponent,
  ],
  templateUrl: './question-bank.component.html',
  styleUrl: './question-bank.component.css',
})
export class QuestionBankComponent {
  private dialog: MatDialog = inject(MatDialog);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);
  questions$: Observable<QuizQuestion[]> = of([]);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;

  questionTypes = QuestionType;
  chipColor = ChipColor;

  constructor() {}

  onSearchResults(questions: QuizQuestion[]) {
    this.questions$ = of(questions);
  }

  onSearchCleared() {
    this.questions$ = of([]);
  }


  editQuestion(index: number, arg0: QuizQuestion) {
    this.logger.debug('Edit Question dialog: ', arg0);
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId,
      question: arg0,
    };
    const dialogRef = this.dialog.open(DlgEditQuestionComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      this.logger.debug('Edit Question dialog closed with result: ', result);
      if (result) {
        this.questions$ = this.questions$.pipe(
          map((questions) => {
            const updatedQuestions = [...questions];
            updatedQuestions[index] = result;
            return updatedQuestions;
          }),
          take(1),
        );
      }
    });
  }

  deleteQuestion(arg0: QuizQuestion) {
    this.logger.debug('Attempting to delete question: ', arg0);
    this.utilsService
      .onRequestConfirmation({
        title: 'Confirm Delete',
        description: 'Are you sure you want to delete this question?',
        action: 'Delete',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.logger.debug('Deletion cancelled by user.');
          return;
        }

        this.logger.debug('User confirmed deletion. Deleting question: ', arg0);

        // 2. Perform the actual deletion
        this.quizService
          .deleteQuestion(this.groupId!, arg0.id!)
          .then(() => {
            this.logger.debug('Question deleted successfully: ', arg0);

            // 3. Update the UI locally without a full re-fetch
            // We re-assign the observable to a new one that filters out the deleted item
            this.questions$ = this.questions$.pipe(
              map((questions) => questions.filter((q) => q.id !== arg0.id)),
              take(1), // Ensure it completes
            );
          })
          .catch((error) => {
            this.logger.error('Error deleting question: ', error);
            this.utilsService.onShowAlert({
              code: 'DELETE_QUESTION_ERROR',
              title: 'Error Deleting Question',
              message: 'Failed to delete the question. Please try again later.',
            } as AppExceptionModel);
          });
      });
  }
}
